import * as React from 'react';
import { IconButton, Table, TableBody, TableCell, TableRow, TableHead } from '@mui/material';
import Add from '@material-ui/icons/Add';
import Remove from '@material-ui/icons/Remove';
import { StockItemUsage } from '../../../models';
import { AutocompleteTextField, NumberInputField } from '.';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { useMemo } from 'react';
import { useEntitiesResult } from '../../../store/entities/hooks';
import { StockEntity } from '../../../store/entities/state';

export interface MultipleStockItemsFieldProps {
    id?: string,
    label?: string,
    values: StockItemUsage[],
    disabled?: boolean,
    onChange: (values: StockItemUsage[]) => void,
}

export const MultipleStockItemsField = (props: MultipleStockItemsFieldProps) => {
    const {
        id,
        label,
        values = [],
        onChange,
        disabled,
    } = props;

    const t = useTranslations();
    const stock = useEntitiesResult<StockEntity>('stock');

    const stockOptions = useMemo(() =>
        stock.map(item => ({ label: item.name, value: item.id })),
        [stock]
    );

    const getStockName = (stockId: string) => {
        const item = stock.find(s => s.id === stockId);
        return item?.name || stockId;
    };

    const onItemChanged = (idx: number, value: StockItemUsage) =>
        onChange(values.map((v, i) => i !== idx ? v : value));

    return (
        <div style={{minHeight: '200px'}}>
            {label && <h5>{label}</h5>}
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t(TK.stockItem)}</TableCell>
                        <TableCell>{t(TK.quantity)}</TableCell>
                        {!disabled && <TableCell/>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {values.map((item, idx) => (
                        <TableRow key={idx}>
                            <TableCell style={{minWidth: '250px', padding: 1}}>
                                <AutocompleteTextField
                                    id={id ? `${id}-${idx}-stock` : undefined}
                                    value={getStockName(item.stockId)}
                                    disabled={disabled || !!item.stockId}
                                    options={stockOptions.map(o => o.label)}
                                    onValueChanged={label => {
                                        const option = stockOptions.find(o => o.label === label);
                                        if (option) {
                                            onItemChanged(idx, {...item, stockId: option.value});
                                        }
                                    }}
                                />
                            </TableCell>
                            <TableCell style={{width: '120px', minWidth: '120px', padding: 1}}>
                                <NumberInputField
                                    id={id ? `${id}-${idx}-quantity` : undefined}
                                    disabled={disabled}
                                    value={item.quantity}
                                    onChange={quantity => onItemChanged(idx, {...item, quantity: quantity || 1})}
                                />
                            </TableCell>
                            {!disabled &&
                                <TableCell style={{width: '70px', minWidth: '70px', padding: 1}}>
                                    <IconButton
                                        id={id ? `${id}-${idx}-remove-button` : undefined }
                                        size="small"
                                        onClick={() => onChange(values.filter((_, i) => i !== idx))}
                                    >
                                        <Remove/>
                                    </IconButton>
                                </TableCell>
                            }
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            { !disabled && <IconButton
                id={id ? `${id}-add-button` : undefined}
                size="small"
                disabled={!stock.length}
                onClick={() => onChange(values.concat({
                    stockId: '',
                    quantity: -1,
                }))}
            >
                <Add/>
            </IconButton> }
        </div>
    );
}
